import { ANALYTICS_FLUSH_MS, SESSION_KEY } from "./config";
import { apiFetch } from "./api";
import { clarityEvent, claritySet, clarityUpgrade } from "@/lib/analytics/clarity";
import { getAttribution } from "@/lib/analytics/dataLayer";

type EventEntry = { type: string; data: Record<string, unknown> };

// Hard whitelist enforced by the pricing-events backend. Anything outside
// this set is silently dropped server-side, so we drop it client-side too
// to keep the queue lean and predictable.
const ALLOWED_EVENT_TYPES = new Set<string>([
  "page_view",
  "date_search",
  "availability_view",
  "item_select",
  "cart_add",
  "cart_remove",
  "checkout_start",
  "checkout_submit",
  "booking_created",
  "booking_error",
  "soldout_view",
  "discount_applied",
]);

// Legacy event names → canonical whitelist names. Anything mapped to null is
// dropped from the analytics queue (Clarity mirroring still runs above).
const EVENT_TYPE_ALIAS: Record<string, string | null> = {
  cart_add_step1: "cart_add",
  cart_edit: null,
  subtype_select: null,
  package_selected: null,
  package_deselected: null,
  aggregate_child_selected: null,
  package_price_missing: null,
  calendar_view: null,
};

const MAX_EVENTS_PER_BATCH = 10;
const ISLAND_PROPERTY = "island";

// Booking funnel events mirrored to Microsoft Clarity for replay filtering.
const CLARITY_EVENTS = new Set<string>([
  "page_view",
  "date_search",
  "availability_view",
  "subtype_select",
  "item_select",
  "cart_add",
  "cart_add_step1",
  "cart_edit",
  "cart_remove",
  "package_selected",
  "discount_applied",
  "checkout_start",
]);

// High-intent events — upgrade the session so Clarity guarantees replay retention.
const CLARITY_UPGRADE_EVENTS = new Set<string>([
  "checkout_start",
  "cart_add",
  "cart_add_step1",
  "discount_applied",
]);

function cartTotalBucket(total: number): string {
  if (total < 100) return "<100";
  if (total < 250) return "100-249";
  if (total < 500) return "250-499";
  if (total < 1000) return "500-999";
  return "1000+";
}

const queue: EventEntry[] = [];
let flushTimer: number | null = null;
let unloadHandler: (() => void) | null = null;
let visHandler: (() => void) | null = null;

function uuidv4(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function getSessionId(): string {
  if (typeof window === "undefined") return "ssr";
  try {
    let sid = window.sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = uuidv4();
      window.sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    return uuidv4();
  }
}

export function getDeviceType(): "mobile" | "tablet" | "desktop" {
  if (typeof window === "undefined") return "desktop";
  const w = window.innerWidth;
  if (w < 768) return "mobile";
  if (w < 1024) return "tablet";
  return "desktop";
}

export function trackEvent(type: string, data: Record<string, unknown> = {}): void {
  // Mirror to Microsoft Clarity (loaded by GTM). All calls are safe no-ops
  // if window.clarity isn't present.
  if (CLARITY_EVENTS.has(type)) {
    clarityEvent(type);
  }
  if (typeof data.cart_total === "number") {
    claritySet("cart_total_bucket", cartTotalBucket(data.cart_total));
  }
  if (typeof data.property === "string") {
    claritySet("property", data.property);
  }
  if (CLARITY_UPGRADE_EVENTS.has(type)) {
    clarityUpgrade(type);
  }

  // Normalize legacy event names, then drop anything not on the backend
  // whitelist. Attribution is sent at the top of the envelope only — never
  // inside event_data.
  const canonical =
    type in EVENT_TYPE_ALIAS ? EVENT_TYPE_ALIAS[type] : type;
  if (!canonical || !ALLOWED_EVENT_TYPES.has(canonical)) return;
  queue.push({ type: canonical, data });
}

function buildEnvelope(events: EventEntry[]) {
  const attribution = getAttribution();
  const referrer =
    attribution.landing_referrer ||
    (typeof document !== "undefined" ? document.referrer : "") ||
    null;
  return {
    session_id: getSessionId(),
    property: ISLAND_PROPERTY,
    device_type: getDeviceType(),
    referrer,
    attribution,
    events,
  };
}

export function flush(): void {
  if (queue.length === 0) return;
  // Backend caps each batch at 10 events; chunk to respect it.
  while (queue.length > 0) {
    const batch = queue.splice(0, MAX_EVENTS_PER_BATCH);
    const body = JSON.stringify(buildEnvelope(batch));
    try {
      apiFetch(`/api/events`, {
        method: "POST",
        body,
        keepalive: true,
      }).catch(() => {});
    } catch {
      // swallow
    }
  }
}

export function startAnalytics(): void {
  if (typeof window === "undefined") return;
  if (flushTimer !== null) return;
  flushTimer = window.setInterval(flush, ANALYTICS_FLUSH_MS);
  unloadHandler = () => flush();
  visHandler = () => {
    if (document.visibilityState === "hidden") flush();
  };
  window.addEventListener("beforeunload", unloadHandler);
  document.addEventListener("visibilitychange", visHandler);
}

export function stopAnalytics(): void {
  if (typeof window === "undefined") return;
  if (flushTimer !== null) {
    window.clearInterval(flushTimer);
    flushTimer = null;
  }
  if (unloadHandler) {
    window.removeEventListener("beforeunload", unloadHandler);
    unloadHandler = null;
  }
  if (visHandler) {
    document.removeEventListener("visibilitychange", visHandler);
    visHandler = null;
  }
  flush();
}
